import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, BatteryMedium, ShieldCheck, CheckCircle2, MessageCircle, Truck, Tag } from "lucide-react";
import { fetchUsatoDetail, euro, USED_STATUS_META, mediaUrl, WA_NUMBER } from "@/lib/store";

const wa = (text) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

export default function UsatoDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => { fetchUsatoDetail(id).then(setD).catch(() => setErr(true)); }, [id]);

  if (err) return (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <p className="text-lg text-neutral-600">Questo dispositivo non è più disponibile.</p>
      <button onClick={() => navigate("/usato")} className="mt-4 inline-flex items-center gap-2 text-neutral-900 underline" data-testid="usato-back-empty"><ArrowLeft className="h-4 w-4" /> Torna all'Usato</button>
    </main>
  );
  if (!d) return <main className="mx-auto max-w-3xl px-4 py-20 text-center text-neutral-400">Caricamento…</main>;

  const meta = USED_STATUS_META[d.stato] || {};
  const title = `${d.marca} ${d.modello}`;
  const specs = [d.memoria, d.colore].filter(Boolean).join(" · ");
  const baseMsg = `Ciao iPhix! Sono interessato all'usato: ${title}${d.memoria ? " " + d.memoria : ""}${d.colore ? " " + d.colore : ""} — ${euro(d.promo_attiva ? d.promo_price_cents : d.prezzo_vendita_cents)} (ID: ${d.id}).`;
  const imgs = d.images || [];

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12" data-testid="usato-detail-page">
      <Helmet>
        <title>{`${title}${d.memoria ? " " + d.memoria : ""} usato ${d.is_apple ? "100% originale Apple " : ""}— iPhix Scafati`}</title>
        <meta name="description" content={`${title}${d.memoria ? " " + d.memoria : ""}${d.colore ? " " + d.colore : ""} usato${d.grado ? ", grado " + d.grado : ""}${d.batteria_pct != null ? ", batteria " + d.batteria_pct + "%" : ""}. ${d.is_apple ? "100% originale Apple, testato e garantito " + (d.warranty_months || 12) + " mesi da iPhix. " : "Testato e garantito " + (d.warranty_months || 12) + " mesi da iPhix. "}Prezzo ${euro(d.promo_attiva ? d.promo_price_cents : d.prezzo_vendita_cents)}.`} />
        <link rel="canonical" href={`https://iphixscafati.it/usato/${d.slug || d.id}`} />
      </Helmet>
      <Link to="/usato" data-testid="usato-back" className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"><ArrowLeft className="h-4 w-4" /> Torna all'Usato</Link>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100">
            {imgs.length > 0 ? (
              <img src={mediaUrl(imgs[active]?.url)} alt={title} className="h-full w-full object-cover" data-testid="usato-main-photo" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-neutral-300">Nessuna foto</div>
            )}
            <span className={`absolute left-3 top-3 rounded-full border px-3 py-1 text-xs font-semibold ${meta.pub || ""}`}>{meta.label || d.stato}</span>
          </div>
          {imgs.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="usato-thumbs">
              {imgs.map((im, i) => (
                <button key={im.id} onClick={() => setActive(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-neutral-900" : "border-transparent"}`}>
                  <img src={mediaUrl(im.url)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900">{title}</h1>
            {d.grado && <span className="shrink-0 rounded-md bg-neutral-900 px-2.5 py-1 text-sm font-bold text-white">Grado {d.grado}</span>}
          </div>
          {specs && <p className="mt-1 text-neutral-500">{specs}</p>}
          <div className="mt-3 flex flex-wrap gap-2" data-testid="usato-badges">
            {d.is_apple && <span className="rounded-full bg-neutral-900 px-3 py-1 text-xs font-bold text-white">100% Originale Apple</span>}
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">✓ Testato</span>
            {d.warranty_active && <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700">🛡️ Testato e garantito {d.warranty_months || 12} mesi da iPhix</span>}
          </div>
          <div className="mt-4 inline-flex items-baseline gap-3">
            {d.promo_attiva ? (<>
              <span className="text-3xl font-bold text-rose-600">{euro(d.promo_price_cents)}</span>
              <span className="text-lg text-neutral-400 line-through">{euro(d.prezzo_vendita_cents)}</span>
              <span className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold text-white">PROMO</span>
            </>) : (
              <span className="inline-flex items-center gap-2 text-3xl font-bold text-neutral-900"><Tag className="h-6 w-6 text-emerald-600" />{euro(d.prezzo_vendita_cents)}</span>
            )}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            {d.batteria_pct != null && <div className="rounded-xl border border-neutral-200 p-3"><div className="flex items-center gap-1.5 text-xs text-neutral-500"><BatteryMedium className="h-4 w-4" /> Batteria</div><div className="mt-1 text-lg font-semibold text-neutral-900">{d.batteria_pct}%</div></div>}
            {d.garanzia && <div className="rounded-xl border border-neutral-200 p-3"><div className="flex items-center gap-1.5 text-xs text-neutral-500"><ShieldCheck className="h-4 w-4" /> Garanzia</div><div className="mt-1 text-lg font-semibold text-neutral-900">{d.garanzia}</div></div>}
          </div>

          {d.condizioni && <div className="mt-5"><h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400">Condizioni estetiche</h2><p className="mt-1 whitespace-pre-line text-neutral-700">{d.condizioni}</p></div>}
          {d.test_effettuati && <div className="mt-4"><h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400">Test effettuati</h2><p className="mt-1 flex items-start gap-2 whitespace-pre-line text-neutral-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{d.test_effettuati}</p></div>}

          {/* CTA */}
          <div className="mt-7 flex flex-col gap-3">
            {d.prenotabile ? (
              <a data-testid="usato-cta-prenota" href={wa(baseMsg + " Vorrei PRENOTARLO / metterlo da parte. È disponibile?")} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-6 py-3.5 font-semibold text-white transition-all hover:scale-[1.01] hover:bg-black">
                Prenota / Richiedi
              </a>
            ) : (
              <div data-testid="usato-reserved-note" className="rounded-full border border-purple-200 bg-purple-50 px-6 py-3.5 text-center font-semibold text-purple-700">Attualmente {meta.label?.toLowerCase() || d.stato.toLowerCase()} — scrivici per la lista d'attesa</div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <a data-testid="usato-cta-whatsapp" href={wa(baseMsg + " Vorrei informazioni / disponibilità e ritiro in negozio.")} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 font-semibold text-white transition-all hover:bg-[#20ba59]">
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
              <a data-testid="usato-cta-spedizione" href={wa(baseMsg + " Vorrei RICHIEDERE LA SPEDIZIONE: potete dirmi costi e tempi?")} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 px-5 py-3 font-semibold text-neutral-800 transition-all hover:border-neutral-900">
                <Truck className="h-5 w-5" /> Richiedi spedizione
              </a>
            </div>
            <p className="text-center text-xs text-neutral-400">Nessun pagamento online: prenotazione e spedizione gestite dal negozio via WhatsApp.</p>
          </div>
        </div>
      </div>
    </main>
  );
}
